import type { ImportProblem, PackagingLevel } from './catalog';
import type { IsoDate, IsoDateTime } from './common';
import type { ProductId, TenantId, WarehouseId } from './ids';
import type { PutawaySuggestion } from './putaway';

export const INBOUND_STATUSES = ['draft', 'expected', 'receiving', 'closed', 'cancelled'] as const;
/** draft (seller still editing) → expected (sent) → receiving (first scan at the dock) → closed; or cancelled before arrival */
export type InboundStatus = (typeof INBOUND_STATUSES)[number];

/** As the seller portal shows them */
export const INBOUND_STATUS_LABELS: Readonly<Record<InboundStatus, string>> = {
  draft: 'Taslak',
  expected: 'Yolda',
  receiving: 'Kısmi kabul edildi',
  closed: 'Tamamlandı',
  cancelled: 'İptal',
};

export const PACKAGING_LEVEL_LABELS: Readonly<Record<PackagingLevel, string>> = {
  each: 'Adet',
  inner: 'İç koli',
  case: 'Koli',
  pallet: 'Palet',
};

export const RECEIPT_CONDITIONS = ['good', 'damaged'] as const;
export type ReceiptCondition = (typeof RECEIPT_CONDITIONS)[number];

export const PUTAWAY_STATUSES = ['open', 'done', 'cancelled'] as const;
export type PutawayStatus = (typeof PUTAWAY_STATUSES)[number];

/** Receiving rules of a warehouse (warehouses.settings.receiving). */
export interface ReceivingPolicy {
  /** How far above the announced quantity a line may be received, in percent. */
  readonly overReceiptTolerancePct: number;
  /** Accept products that are not on the ASN (adds a line with expected 0). */
  readonly acceptUnannounced: boolean;
}

export const DEFAULT_RECEIVING_POLICY: ReceivingPolicy = {
  overReceiptTolerancePct: 10,
  acceptUnannounced: false,
};

export interface InboundShipmentLine {
  readonly id: string;
  readonly lineNo: number;
  readonly productId: ProductId;
  readonly sku: string;
  readonly productName: string;
  readonly expectedQty: number;
  readonly receivedQty: number;
  readonly damagedQty: number;
  readonly lotNo: string | null;
  readonly expiryDate: IsoDate | null;
  /** How the goods travel: expectedQty / unitsPerPack packs of this level */
  readonly packagingLevel: PackagingLevel;
  readonly unitsPerPack: number;
  /** the product's size and weight are known; else the dock measures one unit */
  readonly measured: boolean;
  /** the unit barcode labels are printed with (null: none registered, the SKU is printed) */
  readonly barcode: string | null;
  readonly trackLot: boolean;
  readonly trackExpiry: boolean;
  /** false: the seller wants no labels on the goods (one product card for the shelf instead) */
  readonly unitLabels: boolean;
  /** "Eksik geldi": a manager said the rest of the line is not coming (null: still expected) */
  readonly shortMarkedAt: IsoDateTime | null;
}

/** One unit measured at the dock: millimetres and grams. */
export interface ProductSize {
  readonly lengthMm: number;
  readonly widthMm: number;
  readonly heightMm: number;
  readonly grossWeightG: number;
}

/** ASN (mal kabul bildirimi) with its lines. */
export interface InboundShipment {
  readonly id: string;
  readonly tenantId: TenantId;
  readonly tenantCode: string;
  readonly warehouseId: WarehouseId;
  readonly asnNumber: string;
  readonly reference: string | null;
  readonly status: InboundStatus;
  readonly expectedAt: IsoDate | null;
  readonly carrier: string | null;
  readonly trackingNumber: string | null;
  readonly notes: string | null;
  readonly arrivedAt: IsoDateTime | null;
  readonly closedAt: IsoDateTime | null;
  readonly createdAt: IsoDateTime;
  /** the receiving charge closing booked is invoiced: the receipt can no longer be reopened */
  readonly receivingInvoiced: boolean;
  readonly lines: readonly InboundShipmentLine[];
}

/** An ASN in a list: its lines summed up. */
export type InboundShipmentSummary = Omit<InboundShipment, 'lines'> & {
  readonly lineCount: number;
  readonly expectedUnits: number;
  readonly receivedUnits: number;
};

export interface CreateInboundLine {
  /** Seller SKU or product id */
  readonly sku?: string;
  readonly productId?: string;
  /** Units; or give packaging and packs (4 cases) and the units follow from the product's packaging */
  readonly expectedQty?: number;
  readonly packaging?: PackagingLevel;
  readonly packs?: number;
  readonly lotNo?: string | null;
  readonly expiryDate?: IsoDate | null;
}

export interface CreateInboundRequest {
  readonly warehouseId: string;
  /** Keep it as a draft (taslak) instead of sending it to the warehouse */
  readonly draft?: boolean;
  readonly reference?: string | null;
  readonly expectedAt?: IsoDate | null;
  readonly carrier?: string | null;
  readonly trackingNumber?: string | null;
  readonly notes?: string | null;
  readonly lines: readonly CreateInboundLine[];
}

/** One scan at the dock. */
export interface ReceiveScanRequest {
  /** EAN/GTIN, ITF-14 (one scan = one case) or a GS1-128 koli label carrying lot and expiry */
  readonly barcode?: string;
  /** counted by hand with no barcode read, instead of barcode: a supervisor's call (the API checks the role) */
  readonly productId?: string;
  /** Scans at once (default 1) */
  readonly qty?: number;
  /** Needed for lot/expiry-tracked products unless the GS1 label carries them */
  readonly lotNo?: string | null;
  readonly expiryDate?: IsoDate | null;
  readonly condition?: ReceiptCondition;
  /** the dock confirms more than the tolerance allows: the surplus arrived and is taken in */
  readonly acceptOver?: boolean;
  /** the dock confirms a product the seller did not announce: a line is added for it */
  readonly acceptUnannounced?: boolean;
  /** barcodes the dock taught just before for this scan (an unknown code): geri al forgets them with it */
  readonly taughtBarcodes?: readonly string[];
}

/** An open shipment that expects what was scanned (a product barcode scanned where an ASN number goes). */
export interface ExpectingShipment {
  readonly asnNumber: string;
  readonly tenantCode: string;
  readonly sku: string;
  readonly name: string;
  readonly expectedQty: number;
  readonly countedQty: number;
}

/**
 * inbound: goods at the dock; cancelled_order: units of a cancelled order going back to their bin
 * (rafa iade); customer_return: sellable units of a customer return, from the returns area
 */
export const PUTAWAY_ORIGINS = ['inbound', 'cancelled_order', 'customer_return'] as const;
export type PutawayOrigin = (typeof PUTAWAY_ORIGINS)[number];

export interface PutawayTaskView {
  readonly id: string;
  readonly status: PutawayStatus;
  readonly origin: PutawayOrigin;
  /** Set for put-back tasks of a cancelled order and for customer returns */
  readonly orderNumber: string | null;
  /** RMA number of a customer return */
  readonly rmaNumber: string | null;
  /** The shipment (ASN) the goods were received with, for goods from the dock */
  readonly asnNumber: string | null;
  readonly tenantCode: string;
  readonly productId: ProductId;
  readonly sku: string;
  readonly productName: string;
  readonly qty: number;
  readonly lotNo: string | null;
  readonly expiryDate: IsoDate | null;
  readonly fromLocationCode: string;
  /** Best bin first; the operator scans it to confirm */
  readonly suggestions: readonly PutawaySuggestion[];
}

export interface ReceiveScanResult {
  readonly shipmentId: string;
  readonly asnNumber: string;
  readonly line: InboundShipmentLine;
  readonly receipt: {
    readonly id: string;
    readonly qty: number;
    readonly condition: ReceiptCondition;
    readonly lotNo: string | null;
    readonly expiryDate: IsoDate | null;
    readonly locationCode: string;
  };
  /** Directed putaway for good units; null for damaged ones (they stay in the damaged area) */
  readonly putaway: PutawayTaskView | null;
}

export interface InboundDiscrepancy {
  readonly lineNo: number;
  readonly sku: string;
  readonly expected: number;
  readonly received: number;
  readonly damaged: number;
  /** received + damaged − expected: negative = short, positive = over */
  readonly difference: number;
}

export interface CloseInboundResult {
  readonly shipmentId: string;
  readonly asnNumber: string;
  readonly totals: { readonly expected: number; readonly received: number; readonly damaged: number };
  readonly discrepancies: readonly InboundDiscrepancy[];
}

export interface ConfirmPutawayRequest {
  /** Barcode (LA030201) or code (A-03-2-1) of the bin the goods were put into */
  readonly locationBarcode: string;
  /** Defaults to the whole task; less splits the task */
  readonly qty?: number;
  /** Required when the bin is not the suggested one */
  readonly overrideReason?: string | null;
  /** a supervisor confirms the goods fit although the bin's recorded volume or load says no */
  readonly acceptCapacity?: boolean;
  /**
   * The person confirmed the warning that the bin holds another seller's stock (or another product
   * where the warehouse keeps products apart): "Yine de bu göze koy". Anyone on the floor may.
   */
  readonly acceptMixing?: boolean;
}

/** details of MIXED_BIN: the bin holds what the goods should not mix with; confirming (acceptMixing) puts them there anyway */
export interface MixedBinDetails {
  readonly rejection: 'other_seller' | 'other_product';
  /** every mixing rule the bin breaks (both when another seller's other product lies there and products are kept apart) */
  readonly rules: readonly ('other_seller' | 'other_product')[];
  /** the bin's code */
  readonly bin: string;
  /** other sellers with stock in the bin, by code */
  readonly sellers: readonly string[];
  /** other products in the bin, by SKU */
  readonly skus: readonly string[];
  /** the recorded volume or load says no too: a supervisor may pass both at once (acceptCapacity) */
  readonly capacity: 'volume_exceeded' | 'weight_exceeded' | null;
}

/** A shipment sheet (Excel / CSV) read into lines of the seller's products; what did not match, by row. */
export interface InboundSheetResult {
  /** rows with something in them */
  readonly rows: number;
  readonly lines: readonly {
    readonly row: number;
    readonly productId: string;
    readonly sku: string;
    readonly name: string;
    readonly qty: number;
    readonly lotNo: string | null;
    readonly expiryDate: IsoDate | null;
  }[];
  readonly errors: readonly ImportProblem[];
  readonly ignoredColumns: readonly string[];
}

/** A barcode found on the goods at the dock: the unit's own, or a carton's of N units. */
export interface DockBarcode {
  readonly barcode: string;
  /** 1: the unit's own barcode; more: a carton (koli) of that many units */
  readonly unitsPerScan: number;
}

/** Barcodes found on the goods at the dock, taught to a product of the shipment: its own, its carton's, or both. */
export interface DockBarcodeRequest {
  readonly productId: string;
  readonly barcodes: readonly DockBarcode[];
}

export interface DockBarcodesTaught {
  readonly sku: string;
  /** saved to the product now */
  readonly added: readonly DockBarcode[];
  /** the product had them already, the same way */
  readonly known: readonly DockBarcode[];
}

/** The details of UNKNOWN_BARCODE: a code no product of the shipment's seller carries. */
export interface UnknownBarcodeDetails {
  readonly barcode: string | null;
  /** another seller's product carries it (only that fact, never which product): mixed into the carton? */
  readonly otherSeller: boolean;
}

/** Geri al at the dock: the shipment after the scan was taken back, and the barcodes taught for that scan that went with it. */
export type UndoneReceipt = InboundShipment & { readonly forgottenBarcodes: readonly string[] };

export interface ConfirmPutawayResult {
  readonly taskId: string;
  readonly locationCode: string;
  readonly qty: number;
  readonly followedSuggestion: boolean;
  /** Task holding the units not put away yet, when qty was less than the task */
  readonly remainderTaskId: string | null;
  /** the warnings passed on the way in (mixing confirmed by the person, capacity by a supervisor) */
  readonly overridden: readonly ('other_seller' | 'other_product' | 'volume_exceeded' | 'weight_exceeded')[];
}

// --- corrections by the warehouse's managers (mal kabul ve yerleştirme düzeltmeleri) -------------

export const INBOUND_CORRECTION_KINDS = ['set_qty', 'remove_receipt', 'remove_line', 'remove_task', 'mark_short', 'unmark_short'] as const;
/** set_qty: the counts set by hand; remove_*: units taken off (a scan, the whole line, an open putaway task's); mark_short: "eksik geldi" */
export type InboundCorrectionKind = (typeof INBOUND_CORRECTION_KINDS)[number];

export const INBOUND_CORRECTION_LABELS: Readonly<Record<InboundCorrectionKind, string>> = {
  set_qty: 'Adet düzeltildi',
  remove_receipt: 'Okutma silindi',
  remove_line: 'Kalem silindi',
  remove_task: 'Gelmeyen ürün kabulden düşüldü',
  mark_short: 'Eksik geldi olarak işaretlendi',
  unmark_short: 'Eksik işareti kaldırıldı',
};

/** Where units of a receipt are now, as receiving knows it. */
export interface ReceiptPlace {
  /** dock: the receiving area, waiting for putaway; bin: put away; damaged: the damaged area */
  readonly kind: 'dock' | 'bin' | 'damaged';
  readonly locationCode: string;
  readonly qty: number;
}

/** One scan at the dock still standing (or a count by hand, or units a correction added), as the manager's correction screen lists it. */
export interface InboundReceiptView {
  readonly id: string;
  readonly lineId: string;
  readonly sku: string;
  readonly productName: string;
  /** units the scan counted */
  readonly qty: number;
  /** units of it taken off by corrections since */
  readonly removedQty: number;
  readonly condition: ReceiptCondition;
  readonly lotNo: string | null;
  readonly expiryDate: IsoDate | null;
  /** the code read (null: counted by hand or added by a correction) */
  readonly scannedCode: string | null;
  /** added by a manager's correction */
  readonly byCorrection: boolean;
  readonly receivedBy: string | null;
  readonly receivedAt: IsoDateTime;
  /** where its units are now (empty once none are left) */
  readonly places: readonly ReceiptPlace[];
}

/** A unit move a correction made: off the dock / out of a bin (to null), or into the dock / damaged area (from null). */
export interface CorrectionMove {
  readonly from: string | null;
  readonly to: string | null;
  readonly qty: number;
}

export interface InboundCorrectionView {
  readonly id: string;
  readonly kind: InboundCorrectionKind;
  readonly lineId: string;
  readonly sku: string;
  readonly productName: string;
  readonly expectedQty: number;
  readonly before: { readonly received: number; readonly damaged: number };
  readonly after: { readonly received: number; readonly damaged: number };
  readonly moves: readonly CorrectionMove[];
  readonly note: string | null;
  /** made after the receipt was closed (the seller was told again) */
  readonly shipmentClosed: boolean;
  /** who corrected it (warehouse staff see the name; the seller sees null) */
  readonly correctedBy: string | null;
  readonly correctedAt: IsoDateTime;
}

/**
 * "Adedi düzelt": the units of a line that came, good and damaged, as the manager counted them. Units
 * taken off leave the dock first, then their bin; units added are received like a count by hand.
 */
export interface CorrectLineRequest {
  readonly receivedQty: number;
  readonly damagedQty: number;
  /** what the screen showed: a scan since then refuses the correction (LINE_CHANGED) */
  readonly shownReceivedQty: number;
  readonly shownDamagedQty: number;
  /** lot and expiry of units added, when the product tracks them (default: the line's or its last scan's) */
  readonly lotNo?: string | null;
  readonly expiryDate?: IsoDate | null;
  /** why: the seller sees it with the correction */
  readonly note?: string | null;
}

export interface InboundCorrectionResult {
  readonly shipment: InboundShipment;
  readonly correction: InboundCorrectionView;
}

/** A done putaway as the manager's list shows it (to take it back). */
export type PutawayDoneView = PutawayTaskView & {
  readonly toLocationCode: string;
  readonly completedAt: IsoDateTime;
  readonly completedBy: string | null;
  readonly overrideReason: string | null;
};

/** Goods waiting in the receiving area with no putaway task (one was deleted): a task can be made for them. */
export interface UnplannedStock {
  readonly locationId: string;
  readonly locationCode: string;
  readonly tenantCode: string;
  readonly productId: ProductId;
  readonly sku: string;
  readonly productName: string;
  readonly lotNo: string | null;
  readonly expiryDate: IsoDate | null;
  readonly qty: number;
}

/** An open task sent to another bin: the floor puts it there; what the bin's rules will say then. */
export type RedirectedPutaway = PutawayTaskView & { readonly warning: string | null };
